import {
  ACCEPTED_MIME_TYPES,
  MAX_ATTACHMENTS,
  MAX_FILE_SIZE_MB,
} from "@circle-react-shared/AiConversation/composer/Attachments/constants";
import { useComposerError } from "@circle-react-shared/AiConversation/composer/ComposerError/useComposerError";
import { generateRandomId } from "@circle-react/helpers/generateRandomId";
import { directUploadPath } from "@circle-react/helpers/urlHelpers";
import { DirectUpload } from "@rails/activestorage";
import { useCallback, useState } from "react";

import { t } from "@/i18n-js/instance";

export interface AttachmentFile {
  contentType: string;
  filename: string;
  id: string;
  previewUrl?: string;
  progress: number;
  signedId: string | null;
  status: "uploading" | "completed" | "error";
}

export const useUploadAttachments = ({
  maxFileSizeMb = MAX_FILE_SIZE_MB,
}: { maxFileSizeMb?: number } = {}) => {
  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
  const showError = useComposerError(s => s.showError);

  const addFiles = useCallback(
    (files: File[]) => {
      if (!files.length) return;

      const remaining = MAX_ATTACHMENTS - attachments.length;
      if (files.length > remaining) {
        showError({
          title: t("circle_ai_home.attachments.errors.max_attachments.title", {
            max: MAX_ATTACHMENTS,
          }),
        });
        return;
      }

      if (files.some(f => !ACCEPTED_MIME_TYPES.has(f.type))) {
        showError({
          title: t("circle_ai_home.attachments.errors.invalid_file_type.title"),
          description: t(
            "circle_ai_home.attachments.errors.invalid_file_type.description",
          ),
        });
        return;
      }

      if (files.some(f => f.size > maxFileSizeMb * 1024 * 1024)) {
        showError({
          title: t("circle_ai_home.attachments.errors.file_too_large.title", {
            max: maxFileSizeMb,
          }),
        });
        return;
      }

      const entries: AttachmentFile[] = files.map(f => ({
        id: generateRandomId(),
        filename: f.name,
        contentType: f.type,
        previewUrl: URL.createObjectURL(f),
        progress: 0,
        signedId: null,
        status: "uploading",
      }));

      setAttachments(prev => [...prev, ...entries]);

      files.forEach((file, i) => {
        const { id } = entries[i];
        const patch = (update: Partial<AttachmentFile>) =>
          setAttachments(prev =>
            prev.map(a => (a.id === id ? { ...a, ...update } : a)),
          );

        const upload = new DirectUpload(file, directUploadPath(), {
          directUploadWillStoreFileWithXHR: xhr => {
            xhr.upload.addEventListener("progress", e => {
              if (e.lengthComputable && e.total > 0) {
                patch({ progress: (e.loaded / e.total) * 100 });
              }
            });
          },
        });
        upload.create((error, blob) => {
          if (error) {
            showError({
              title: t("circle_ai_home.attachments.errors.upload_failed.title"),
              description: t(
                "circle_ai_home.attachments.errors.upload_failed.description",
              ),
            });
            patch({ status: "error" });
          } else {
            patch({
              status: "completed",
              progress: 100,
              signedId: blob.signed_id,
            });
          }
        });
      });
    },
    [attachments.length, maxFileSizeMb, showError],
  );

  const removeAttachment = useCallback((id: string) => {
    setAttachments(prev => {
      const removed = prev.find(a => a.id === id);
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter(a => a.id !== id);
    });
  }, []);

  // Does not revoke blob URLs. They may still be referenced by optimistic
  // message previews in the thread after send.
  const clearAttachments = useCallback(() => {
    setAttachments([]);
  }, []);

  const isUploading = attachments.some(a => a.status === "uploading");

  const signedIds = attachments
    .map(a => a.signedId)
    .filter((id): id is string => id !== null);

  return {
    attachments,
    isUploading,
    signedIds,
    addFiles,
    removeAttachment,
    clearAttachments,
  };
};
